/* global fastSpring, fastSpringCallBack, fastspring */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, InputNumber, message, Tag, Spin, Divider, Radio, Space, Select, Modal } from 'antd';
import { WalletOutlined, CreditCardOutlined, CheckCircleOutlined, ThunderboltOutlined, DollarOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { userAPI } from '../services/api';
import { t } from '../utils/i18n';

// ============ FastSpring 配置（从后端动态加载）============
const DEFAULT_CONFIG = {
  test: {
    storefront: 'blueapi.test.onfastspring.com',
    returnUrl: 'http://localhost:3001/dashboard/recharge',
    cancelUrl: 'http://localhost:3001/dashboard/recharge',
    customProduct: 'blueapi-ai-product',
    timeout: 600,
    pollInterval: 2000,
  },
  production: {
    storefront: 'blueapi.onfastspring.com',
    returnUrl: '/dashboard/recharge',
    cancelUrl: '/dashboard/recharge',
    customProduct: 'blueapi-ai-product',
    timeout: 600,
    pollInterval: 2000,
  },
};

// 支付状态
const PAYMENT_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled',
};

const RECHARGE = () => {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [customAmount, setCustomAmount] = useState(10);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [availableMethods, setAvailableMethods] = useState([]);
  const [buttonLoading, setButtonLoading] = useState({});
  const [configLoading, setConfigLoading] = useState(true);
  const [fastSpringConfig, setFastSpringConfig] = useState(DEFAULT_CONFIG.test);
  const [currentEnv, setCurrentEnv] = useState('test');
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.PENDING);
  const [currentOrderNo, setCurrentOrderNo] = useState('');
  const [currentAmount, setCurrentAmount] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const paymentWindowRef = useRef(null);
  const pollIntervalRef = useRef(2000);
  const timeoutSecondsRef = useRef(600);

  const presetAmounts = [10, 20, 50, 100];

  // ============ 核心回调函数（必须在 useEffect 之前定义）============

  // 停止轮询 - 必须在最前面定义，因为其他 useEffect 可能依赖它
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // 关闭支付窗口
  const closePaymentWindow = useCallback(() => {
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
      paymentWindowRef.current = null;
    }
  }, []);

  // 刷新余额 - 其他 useEffect 可能依赖它
  const fetchBalance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userAPI.getQuota();
      if (response.code === 0) {
        setBalance(parseFloat(response.data.balance || 0));
      }
    } catch (error) {
      console.error(t('fetch_balance_error') || 'Failed to fetch balance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 自动检测环境
  const detectEnvironment = useCallback(() => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
      return 'test';
    }
    return 'production';
  }, []);

  // 从后端获取 FastSpring 配置 - useEffect 依赖它
  const fetchFastSpringConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const env = detectEnvironment();
      setCurrentEnv(env);
      
      const response = await fetch(`/api/payment-config?env=${env}`);
      const data = await response.json();
      
      if (data.code === 0 && data.data) {
        const config = {
          storefront: data.data.storefront || DEFAULT_CONFIG[env].storefront,
          returnUrl: data.data.return_url || DEFAULT_CONFIG[env].returnUrl,
          cancelUrl: data.data.cancel_url || DEFAULT_CONFIG[env].cancelUrl,
          productMap: data.data.product_map || DEFAULT_CONFIG[env].productMap,
          customProduct: data.data.custom_product || 'blueapi-ai-product',
          timeout: data.data.timeout || DEFAULT_CONFIG[env].timeout || 600,
          pollInterval: data.data.poll_interval || DEFAULT_CONFIG[env].pollInterval || 2000,
        };
        setFastSpringConfig(config);
        // 保存到 refs
        pollIntervalRef.current = config.pollInterval;
        timeoutSecondsRef.current = config.timeout;
      } else {
        // 使用默认配置
        setFastSpringConfig(DEFAULT_CONFIG[env]);
        pollIntervalRef.current = DEFAULT_CONFIG[env].pollInterval;
        timeoutSecondsRef.current = DEFAULT_CONFIG[env].timeout;
      }
    } catch (error) {
      console.error('Failed to fetch FastSpring config:', error);
      // 使用默认配置
      const env = detectEnvironment();
      setFastSpringConfig(DEFAULT_CONFIG[env]);
      pollIntervalRef.current = DEFAULT_CONFIG[env].pollInterval;
      timeoutSecondsRef.current = DEFAULT_CONFIG[env].timeout;
    } finally {
      setConfigLoading(false);
    }
  }, [detectEnvironment]);

  // 获取产品 ID - 统一使用 blueapi-ai-product，通过 customPrice 锁定金额
  const getFastSpringProductId = useCallback(() => {
    return fastSpringConfig.customProduct || 'blueapi-ai-product';
  }, [fastSpringConfig]);

  // 取消订单 - 调用后端取消（pollOrderStatus 依赖它）
  const cancelOrderOnBackend = useCallback(async (orderNo) => {
    if (!orderNo) return;
    try {
      const token = localStorage.getItem('user_token');
      const res = await fetch(`/api/orders/by-no/${orderNo}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const result = await res.json();
      if (result.code === 0) {
        console.log('Order cancelled on backend:', orderNo);
      } else {
        console.warn('Failed to cancel order on backend:', orderNo, result.message);
      }
    } catch (error) {
      console.error('Failed to cancel order on backend:', error);
    }
  }, []);

  // 轮询订单状态 - handleFastSpringCheckout 依赖它
  const pollOrderStatus = useCallback((orderNo, amount) => {
    stopPolling(); // 先停止之前的轮询
    
    setCurrentOrderNo(orderNo);
    setCurrentAmount(amount);
    setPaymentStatus(PAYMENT_STATUS.PENDING);
    setPaymentModalVisible(true);
    
    const timeoutSeconds = timeoutSecondsRef.current;
    const pollInterval = pollIntervalRef.current;
    let elapsed = 0;
    setRemainingTime(timeoutSeconds);

    // 启动倒计时
    countdownRef.current = setInterval(() => {
      elapsed += 1;
      setRemainingTime(Math.max(0, timeoutSeconds - elapsed));
    }, 1000);

    // 启动轮询
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/fastspring/order-status?orderNo=${orderNo}`);
        const result = await res.json();

        if (result.code === 0 && result.data) {
          const status = result.data.status;
          console.log('Order status:', status);

          if (status === 'succeeded') {
            stopPolling();
            setPaymentStatus(PAYMENT_STATUS.SUCCESS);
            fetchBalance();
            message.success(t('payment_success') || 'Payment successful! Balance added');
            return;
          }

          if (status === 'failed') {
            stopPolling();
            setPaymentStatus(PAYMENT_STATUS.FAILED);
            message.error(t('payment_failed') || 'Payment failed');
            return;
          }
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, pollInterval);

    // 超时处理 - 标记订单超时
    timeoutRef.current = setTimeout(async () => {
      stopPolling();
      // 调用后端取消超时订单
      cancelOrderOnBackend(orderNo);
      setPaymentStatus(PAYMENT_STATUS.TIMEOUT);
    }, timeoutSeconds * 1000);
  }, [stopPolling, fetchBalance, cancelOrderOnBackend]);

  // 取消订单（前端操作）- Modal 的 onCancel 依赖它
  const handleCancelOrder = useCallback(() => {
    stopPolling();
    closePaymentWindow();
    // 调用后端取消订单
    cancelOrderOnBackend(currentOrderNo);
    setPaymentStatus(PAYMENT_STATUS.CANCELLED);
    message.info(t('payment_cancelled') || 'Payment cancelled');
    setTimeout(() => {
      setPaymentModalVisible(false);
    }, 2000);
  }, [stopPolling, closePaymentWindow, currentOrderNo, cancelOrderOnBackend]);

  // ============ useEffect 初始化 =============
  useEffect(() => {
    fetchFastSpringConfig();
    fetchBalance();
    fetchPaymentMethods();

    // 处理 URL 参数
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('paypal_success') === 'true') {
      message.success(t('payment_success') || 'Payment successful! Balance added');
      fetchBalance();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('paypal_cancel') === 'true') {
      message.info(t('payment_cancelled') || 'Payment cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('fastspring_success') === 'true') {
      const orderId = urlParams.get('order');
      const amount = urlParams.get('amount');
      if (orderId || amount) {
        message.success(`${t('payment_success') || 'Payment successful!'} Order: ${orderId || 'N/A'}, Amount: $${amount || 'N/A'}`);
      } else {
        message.success(t('payment_success') || 'Payment successful! Balance added');
      }
      fetchBalance();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('fastspring_cancel') === 'true') {
      // 停止轮询
      stopPolling();
      
      // 从 URL 中获取订单号并取消订单
      const orderNo = urlParams.get('orderNo');
      if (orderNo) {
        // 调用后端取消订单
        const token = localStorage.getItem('user_token');
        fetch(`/api/orders/${orderNo}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
          .then(res => res.json())
          .then(result => {
            if (result.code === 0) {
              message.info(t('payment_cancelled') || 'Payment cancelled. Order has been cancelled.');
            } else {
              message.info(t('payment_cancelled') || 'Payment cancelled');
            }
          })
          .catch(err => {
            console.error('Failed to cancel order:', err);
            message.info(t('payment_cancelled') || 'Payment cancelled');
          });
      } else {
        message.info(t('payment_cancelled') || 'Payment cancelled');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchFastSpringConfig, fetchBalance, stopPolling]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await userAPI.getPaymentMethods();
      if (response.code === 0 && response.data?.methods) {
        setAvailableMethods(response.data.methods);
        // 提取 Airwallex 环境配置
        const awx = response.data.methods.find(m => m.id === 'airwallex');
        if (awx?.env) {
          window.__airwallex_env = awx.env;
        }
        if (response.data.methods.length > 0) {
          setPaymentMethod(response.data.methods[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      setAvailableMethods([
        { id: 'stripe', name: 'Credit/Debit Card', icon: 'creditcard' },
        { id: 'paypal', name: 'PayPal', icon: 'paypal' },
      ]);
    }
  };

  // ============ 其他 useEffect 和功能函数 =============

  // 检测支付窗口是否已关闭（通过轮询检测）
  const checkPaymentWindowClosed = useCallback(() => {
    // 使用一个隐藏的 iframe 来检测新标签页是否还在
    // 由于安全限制，我们只能检测当前页面是否被用户交互过
    // 这里我们通过一个间接的方法：检查订单创建后是否有任何用户活动
    
    // 检查当前标签页是否可见
    if (document.visibilityState === 'hidden') {
      // 用户可能切换到了支付页面所在的标签
      console.log('User might have switched to payment window');
    }
    
    // 如果用户关闭了模态框且订单还在进行中，认为用户可能关闭了支付窗口
    return false; // 返回 false 表示无法确定
  }, []);

  // 添加页面可见性变化监听
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && paymentStatus === PAYMENT_STATUS.PENDING) {
        // 用户回到了当前页面，可以检查一下订单状态
        console.log('User returned to payment page, checking order status...');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [paymentStatus]);

  // 添加页面卸载处理 - 用户关闭浏览器或离开页面时取消未支付的订单
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // 只有当有 pending 订单时才处理
      if (paymentStatus === PAYMENT_STATUS.PENDING && currentOrderNo) {
        // 同步调用后端取消订单（使用 sendBeacon 确保在页面卸载前发送）
        const data = JSON.stringify({ orderNo: currentOrderNo });
        navigator.sendBeacon('/api/orders/by-no/' + currentOrderNo, new Blob([data], { type: 'application/json' }));
        console.log('Cancelling order on page unload:', currentOrderNo);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [paymentStatus, currentOrderNo]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // FastSpring 支付 - 使用链接方式打开新标签
  // 使用 customPrice + quantityBehavior: "LOCK" 锁定价格和数量
  const handleFastSpringCheckout = useCallback(async (productId, options = {}) => {
    const { amount } = options;

    console.log('=== FastSpring Checkout ===');
    console.log('productId:', productId);
    console.log('amount:', amount);

    // 显示加载状态
    const loadingKey = message.loading(t('creating_order') || 'Creating payment session...', 0);

    try {
      // 获取用户 token
      const token = localStorage.getItem('user_token');
      if (!token) {
        loadingKey();
        message.error('Please login first');
        return;
      }

      // 调用后端接口获取 checkoutUrl
      const response = await fetch('/api/fastspring/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, amount: amount }),
        credentials: 'include',
      });

      const data = await response.json();
      loadingKey();

      if (data.code !== 0 || !data.data) {
        message.error(data.message || 'Failed to create payment session');
        return;
      }

      const checkoutUrl = data.data.checkoutUrl;
      const orderNo = data.data.orderNo;

      if (!checkoutUrl) {
        message.error('Failed to get checkout URL');
        return;
      }

      console.log('checkoutUrl:', checkoutUrl);
      console.log('orderNo:', orderNo);

      // 创建隐藏的链接并点击，在新标签页打开
      const link = document.createElement('a');
      link.href = checkoutUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();

      // 显示等待 Modal 并开始轮询
      pollOrderStatus(orderNo, amount);

    } catch (error) {
      console.error('FastSpring payment error:', error);
      message.error('Payment error: ' + (error?.message || 'Please try again later'));
    }
  }, [pollOrderStatus]);

  // 处理充值
  const handleRecharge = async (amount) => {
    if (amount < 1) {
      message.warning(t('min_recharge') || 'Minimum recharge amount is $1');
      return;
    }

    setProcessing(true);
    setButtonLoading(prev => ({ ...prev, [amount]: true }));
    
    try {
      if (paymentMethod === 'fastspring') {
        const productId = getFastSpringProductId();
        handleFastSpringCheckout(productId, { amount });
        setProcessing(false);
        setButtonLoading(prev => ({ ...prev, [amount]: false }));
        return;
      }

      // Airwallex HPP 支付 - 先开窗口再调API（避免被浏览器拦截）
      if (paymentMethod === 'airwallex') {
        const env = window.__airwallex_env || 'prod';
        // 先同步打开窗口（必须在用户点击的同步调用栈中）
        const win = window.open('/airwallex-checkout.html?loading=true&env=' + env, '_blank');
        if (win) paymentWindowRef.current = win;

        const loadingKey = message.loading(t('creating_order') || 'Creating payment order...', 0);
        const response = await userAPI.recharge(amount, 'usd', paymentMethod);
        loadingKey();

        if (response.code === 0) {
          const { intent_id, client_secret, order_no } = response.data;
          // API 成功后，把参数传给已打开的窗口
          if (win && !win.closed) {
            win.location.href = '/airwallex-checkout.html?intent_id=' + encodeURIComponent(intent_id) + '&client_secret=' + encodeURIComponent(client_secret) + '&currency=usd&env=' + env;
          } else {
            // 窗口被关了，打开新的
            handleAirwallexCheckout(intent_id, client_secret, 'usd', order_no, amount);
          }
          // 显示等待弹窗 + 轮询
          pollAirwallexOrderStatus(order_no, amount);
        } else {
          if (win && !win.closed) win.close();
          message.error(response.message || t('create_order_failed') || 'Failed to create payment order');
        }
        setProcessing(false);
        setButtonLoading(prev => ({ ...prev, [amount]: false }));
        return;
      }

      // Stripe/PayPal 使用后端 API
      const loadingKey = message.loading(t('creating_order') || 'Creating payment order...', 0);
      const response = await userAPI.recharge(amount, 'usd', paymentMethod);
      loadingKey();

      if (response.code === 0) {
        if (response.data?.approval_url) {
          window.location.href = response.data.approval_url;
        } else if (response.data?.session_url) {
          window.location.href = response.data.session_url;
        } else {
          message.error(t('create_order_failed') || 'Failed to create payment order');
        }
      } else {
        message.error(response.message || t('create_order_failed') || 'Failed to create payment order');
      }
    } catch (error) {
      console.error(t('recharge_failed') || 'Recharge failed:', error);
      message.error(t('recharge_error') || 'Recharge request failed, please try again later');
    } finally {
      setProcessing(false);
      setButtonLoading(prev => ({ ...prev, [amount]: false }));
    }
  };

  // 处理自定义金额充值
  const handleCustomRecharge = async () => {
    if (customAmount < 1) {
      message.warning(t('min_recharge') || 'Minimum recharge amount is $1');
      return;
    }

    setProcessing(true);
    setButtonLoading(prev => ({ ...prev, custom: true }));
    
    try {
      if (paymentMethod === 'fastspring') {
        // 统一使用 blueapi-ai-product 产品，通过 customPrice 锁定任意金额
        const productId = 'blueapi-ai-product';
        handleFastSpringCheckout(productId, { price: customAmount, amount: customAmount });
        setProcessing(false);
        setButtonLoading(prev => ({ ...prev, custom: false }));
        return;
      }

      // Airwallex HPP - 先开窗口再调API
      if (paymentMethod === 'airwallex') {
        const env = window.__airwallex_env || 'prod';
        const win = window.open('/airwallex-checkout.html?loading=true&env=' + env, '_blank');
        if (win) paymentWindowRef.current = win;

        const loadingKey = message.loading(t('creating_order') || 'Creating payment order...', 0);
        const response = await userAPI.recharge(customAmount, 'usd', paymentMethod);
        loadingKey();

        if (response.code === 0) {
          const { intent_id, client_secret, order_no } = response.data;
          if (win && !win.closed) {
            win.location.href = '/airwallex-checkout.html?intent_id=' + encodeURIComponent(intent_id) + '&client_secret=' + encodeURIComponent(client_secret) + '&currency=usd&env=' + env;
          }
          pollAirwallexOrderStatus(order_no, customAmount);
        } else {
          if (win && !win.closed) win.close();
          message.error(response.message || t('create_order_failed') || 'Failed to create payment order');
        }
        setProcessing(false);
        setButtonLoading(prev => ({ ...prev, custom: false }));
        return;
      }

      // Stripe/PayPal
      const loadingKey = message.loading(t('creating_order') || 'Creating payment order...', 0);
      const response = await userAPI.recharge(customAmount, 'usd', paymentMethod);
      loadingKey();

      if (response.code === 0) {
        if (response.data?.approval_url) {
          window.location.href = response.data.approval_url;
        } else if (response.data?.session_url) {
          window.location.href = response.data.session_url;
        } else {
          message.error(t('create_order_failed') || 'Failed to create payment order');
        }
      } else {
        message.error(response.message || t('create_order_failed') || 'Failed to create payment order');
      }
    } catch (error) {
      console.error(t('recharge_failed') || 'Recharge failed:', error);
      message.error(t('recharge_error') || 'Recharge request failed, please try again later');
    } finally {
      setProcessing(false);
      setButtonLoading(prev => ({ ...prev, custom: false }));
    }
  };

  const renderPaymentIcon = (icon, style = {}) => {
    switch (icon) {
      case 'paypal':
        return <DollarOutlined style={{ fontSize: 20, color: '#003087', ...style }} />;
      case 'fastspring':
        return <CreditCardOutlined style={{ fontSize: 20, color: '#00a86b', ...style }} />;
      case 'airwallex':
        return <GlobalOutlined style={{ fontSize: 20, color: '#612fff', ...style }} />;
      default:
        return <CreditCardOutlined style={{ fontSize: 20, ...style }} />;
    }
  };


  // Airwallex 支付 - 新窗口打开 + 弹窗等待
  const handleAirwallexCheckout = (intentId, clientSecret, currency, orderNo, amount) => {
    const env = window.__airwallex_env || 'prod';
    const checkoutUrl = `/airwallex-checkout.html?intent_id=${encodeURIComponent(intentId)}&client_secret=${encodeURIComponent(clientSecret)}&currency=${encodeURIComponent(currency)}&env=${encodeURIComponent(env)}`;

    // 新窗口打开支付页面
    const win = window.open(checkoutUrl, '_blank');
    if (win) {
      paymentWindowRef.current = win;
    }

    // 显示等待弹窗 + 开始轮询
    pollAirwallexOrderStatus(orderNo, amount);
  };

  // Airwallex 订单轮询（复用同一套 Modal UI）
  const pollAirwallexOrderStatus = (orderNo, amount) => {
    stopPolling();

    setCurrentOrderNo(orderNo);
    setCurrentAmount(amount);
    setPaymentStatus(PAYMENT_STATUS.PENDING);
    setPaymentModalVisible(true);

    const timeoutSeconds = timeoutSecondsRef.current;
    const pollInterval = pollIntervalRef.current;
    let elapsed = 0;
    setRemainingTime(timeoutSeconds);

    countdownRef.current = setInterval(() => {
      elapsed += 1;
      setRemainingTime(Math.max(0, timeoutSeconds - elapsed));
    }, 1000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/airwallex/order-status?orderNo=${orderNo}`);
        const result = await res.json();

        if (result.code === 0 && result.data) {
          const status = result.data.status;

          if (status === 'succeeded') {
            stopPolling();
            closePaymentWindow();
            setPaymentStatus(PAYMENT_STATUS.SUCCESS);
            fetchBalance();
            message.success(t('payment_success') || 'Payment successful! Balance added');
            return;
          }

          if (status === 'failed') {
            stopPolling();
            closePaymentWindow();
            setPaymentStatus(PAYMENT_STATUS.FAILED);
            message.error(t('payment_failed') || 'Payment failed');
            return;
          }

          if (status === 'cancelled') {
            stopPolling();
            closePaymentWindow();
            setPaymentStatus(PAYMENT_STATUS.CANCELLED);
            return;
          }
        }
      } catch (error) {
        console.error('Airwallex poll error:', error);
      }
    }, pollInterval);

    timeoutRef.current = setTimeout(async () => {
      stopPolling();
      closePaymentWindow();
      cancelOrderOnBackend(orderNo);
      setPaymentStatus(PAYMENT_STATUS.TIMEOUT);
      message.warning(t('payment_timeout') || 'Payment timeout, order cancelled');
    }, timeoutSeconds * 1000);
  };

  return (
    <div className="user-page-wrapper">
      <div className="user-page-container">
        {/* 页面标题 */}
        <div className="page-title">
          <div className="title-icon">
            <WalletOutlined />
          </div>
          {t('top_up_title')}
          {/* 配置状态指示 */}
          <Tag color={
            paymentMethod === 'airwallex' ? (window.__airwallex_env === 'demo' ? 'orange' : 'green') :
            currentEnv === 'test' ? 'orange' : 'green'
          } style={{ marginLeft: 12 }}>
            {paymentMethod === 'airwallex' ? (window.__airwallex_env === 'demo' ? 'DEMO' : 'PROD') :
             currentEnv === 'test' ? 'TEST' : 'PROD'}
          </Tag>
          {configLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
        </div>

        <Spin spinning={loading}>
          {/* 当前余额 */}
          <div className="content-section">
            <div className="section-header">
              <div className="section-title">
                <WalletOutlined />
                <span>{t('current_balance') || 'Current Balance'}</span>
              </div>
            </div>
            <div className="section-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 600, color: '#1677ff' }}>
                    ${Math.floor(balance * 100) / 100}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Tag color="green" style={{ marginBottom: 8 }}>
                    <CheckCircleOutlined /> {t('account_normal') || 'Account Normal'}
                  </Tag>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>
                    {t('instant_credit') || 'Instant credit'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 支付方式选择 */}
          <div className="content-section">
            <div className="section-header">
              <div className="section-title">
                <CreditCardOutlined />
                <span>{t('select_payment_method') || 'Select Payment Method'}</span>
              </div>
            </div>
            <div className="section-body">
              <Radio.Group value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <Space size="large">
                  {availableMethods.map((method) => (
                    <Radio.Button 
                      key={method.id} 
                      value={method.id} 
                      style={{ padding: '12px 24px', height: 'auto' }}
                    >
                      <Space>
                        {renderPaymentIcon(method.icon)}
                        <span>{method.name}</span>
                      </Space>
                    </Radio.Button>
                  ))}
                </Space>
              </Radio.Group>
            </div>
          </div>

          {/* 预设金额 */}
          <div className="content-section">
            <div className="section-header">
              <div className="section-title">
                <DollarOutlined />
                <span>{t('select_amount')}</span>
              </div>
            </div>
            <div className="section-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 12, marginBottom: 20 }}>
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    className="uf-btn-default"
                    style={{
                      textAlign: 'center',
                      padding: '12px 16px',
                      cursor: buttonLoading[amount] ? 'wait' : 'pointer',
                      opacity: buttonLoading[amount] ? 0.7 : 1,
                    }}
                    onClick={() => handleRecharge(amount)}
                    disabled={buttonLoading[amount]}
                  >
                    {buttonLoading[amount] ? (
                      <Spin size="small" />
                    ) : (
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#1677ff' }}>
                        ${amount}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <Divider style={{ margin: '20px 0' }}>{t('or_custom_amount') || 'Or custom amount'}</Divider>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center' }}>
                <InputNumber
                  value={customAmount}
                  onChange={setCustomAmount}
                  min={1}
                  max={10000}
                  precision={2}
                  prefix="$"
                  style={{ width: 200 }}
                  size="large"
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleCustomRecharge}
                  loading={buttonLoading.custom}
                  disabled={buttonLoading.custom}
                  icon={paymentMethod === 'paypal' ? <DollarOutlined /> : <CreditCardOutlined />}
                >
                  {t('recharge_now') || 'Recharge Now'}
                </Button>
              </div>
            </div>
          </div>

          {/* 支付说明 */}
          <div className="content-section" style={{ borderBottom: 'none', marginBottom: 0 }}>
            <div className="section-header">
              <div className="section-title">
                <ThunderboltOutlined />
                <span>{t('payment_info') || 'Payment Information'}</span>
              </div>
            </div>
            <div className="section-body">
              <ul style={{ margin: 0, paddingLeft: 20, color: '#6b7280' }}>
                <li>{t('payment_info_1') || 'Secure payment via Stripe'}</li>
                <li>{t('payment_info_2') || 'Supports PayPal account payment'}</li>
                <li>{t('payment_info_3') || 'Instant credit, available for all models'}</li>
                <li>{t('payment_info_4') || 'Balance never expires'}</li>
                <li>{t('payment_info_5') || 'For questions, please contact support'}</li>
              </ul>
            </div>
          </div>
        </Spin>

        {/* 支付状态 Modal */}
        <Modal
          title={
            paymentStatus === PAYMENT_STATUS.PENDING ? (
              <span><ClockCircleOutlined style={{ color: '#1677ff' }} /> Payment Processing</span>
            ) : paymentStatus === PAYMENT_STATUS.SUCCESS ? (
              <span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Payment Successful</span>
            ) : paymentStatus === PAYMENT_STATUS.FAILED ? (
              <span><CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Payment Failed</span>
            ) : paymentStatus === PAYMENT_STATUS.TIMEOUT ? (
              <span><ExclamationCircleOutlined style={{ color: '#faad14' }} /> Payment Timeout</span>
            ) : (
              <span><CloseCircleOutlined style={{ color: '#faad14' }} /> Payment Cancelled</span>
            )
          }
          open={paymentModalVisible}
          footer={
            paymentStatus === PAYMENT_STATUS.PENDING ? (
              <Button danger onClick={handleCancelOrder}>
                Cancel Payment
              </Button>
            ) : (
              <Button type="primary" onClick={() => setPaymentModalVisible(false)}>
                Close
              </Button>
            )
          }
          closable={paymentStatus !== PAYMENT_STATUS.PENDING}
          maskClosable={false}
          keyboard={true}
          onCancel={paymentStatus === PAYMENT_STATUS.PENDING ? handleCancelOrder : undefined}
        >
          {paymentStatus === PAYMENT_STATUS.PENDING && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, fontSize: 16 }}>Please complete payment in the opened window</p>
              <p style={{ color: '#999', marginTop: 8 }}>
                This page will update automatically when payment is confirmed
              </p>
              
              {/* 订单信息 */}
              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 8, 
                marginTop: 16,
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#666' }}>Order:</span>
                  <span style={{ fontFamily: 'monospace' }}>{currentOrderNo}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#666' }}>Amount:</span>
                  <span style={{ fontWeight: 600, color: '#1677ff' }}>${currentAmount}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#666' }}>Time remaining:</span>
                  <span style={{
                    color: remainingTime < 60 ? '#ff4d4f' : '#666',
                    fontWeight: remainingTime < 60 ? 600 : 400
                  }}>
                    {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>
              <p style={{ color: '#faad14', fontSize: 12, marginTop: 8 }}>
                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                Order will be automatically cancelled if payment is not completed within {Math.ceil(timeoutSecondsRef.current / 60)} minutes
              </p>

              {/* 倒计时进度条 */}
              <div style={{ marginTop: 16 }}>
                <div style={{ 
                  height: 4, 
                  background: '#e5e5e5', 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    background: remainingTime < 60 ? '#ff4d4f' : '#1677ff',
                    width: `${(remainingTime / timeoutSecondsRef.current) * 100}%`,
                    transition: 'width 1s linear'
                  }} />
                </div>
              </div>

              <p style={{ color: '#999', fontSize: 12, marginTop: 16 }}>
                If you have completed payment, please wait a moment for confirmation.<br/>
                Do not close this page.
              </p>
            </div>
          )}
          
          {paymentStatus === PAYMENT_STATUS.SUCCESS && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
              <p style={{ fontSize: 20, marginTop: 16, color: '#52c41a', fontWeight: 600 }}>
                Payment Successful!
              </p>
              <p style={{ color: '#666', marginTop: 8 }}>
                Your balance has been updated. You can now use our services.
              </p>
              <div style={{ 
                background: '#f6ffed', 
                padding: 12, 
                borderRadius: 8, 
                marginTop: 16,
                border: '1px solid #b7eb8f'
              }}>
                <span style={{ color: '#52c41a' }}>Order: {currentOrderNo}</span>
                <span style={{ color: '#52c41a', marginLeft: 16 }}>Amount: ${currentAmount}</span>
              </div>
            </div>
          )}
          
          {paymentStatus === PAYMENT_STATUS.FAILED && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CloseCircleOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
              <p style={{ fontSize: 20, marginTop: 16, color: '#ff4d4f', fontWeight: 600 }}>
                Payment Failed
              </p>
              <p style={{ color: '#666', marginTop: 8 }}>
                Your payment was not completed. This could be due to:<br/>
                • Payment method declined<br/>
                • Insufficient funds<br/>
                • Transaction cancelled
              </p>
              <Button 
                type="primary" 
                style={{ marginTop: 16 }}
                onClick={() => {
                  setPaymentModalVisible(false);
                  // 可以重新发起支付
                }}
              >
                Try Again
              </Button>
            </div>
          )}
          
          {paymentStatus === PAYMENT_STATUS.TIMEOUT && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <ExclamationCircleOutlined style={{ fontSize: 64, color: '#faad14' }} />
              <p style={{ fontSize: 20, marginTop: 16, color: '#faad14', fontWeight: 600 }}>
                Payment Timeout
              </p>
              <p style={{ color: '#666', marginTop: 8 }}>
                The payment window has timed out. If you have already completed payment,<br/>
                please contact support for assistance.
              </p>
              <div style={{ 
                background: '#fffbe6', 
                padding: 12, 
                borderRadius: 8, 
                marginTop: 16,
                border: '1px solid #ffe58f'
              }}>
                <span style={{ color: '#faad14' }}>Order: {currentOrderNo}</span>
                <span style={{ color: '#faad14', marginLeft: 16 }}>Amount: ${currentAmount}</span>
              </div>
              <p style={{ color: '#999', fontSize: 12, marginTop: 16 }}>
                Note: If you completed the payment, your balance will be updated automatically<br/>
                once the payment is confirmed.
              </p>
            </div>
          )}

          {paymentStatus === PAYMENT_STATUS.CANCELLED && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CloseCircleOutlined style={{ fontSize: 64, color: '#faad14' }} />
              <p style={{ fontSize: 20, marginTop: 16, color: '#faad14', fontWeight: 600 }}>
                Payment Cancelled
              </p>
              <p style={{ color: '#666', marginTop: 8 }}>
                You have cancelled the payment. No charges were made.
              </p>
              <Button 
                type="primary" 
                style={{ marginTop: 16 }}
                onClick={() => {
                  setPaymentModalVisible(false);
                }}
              >
                Back to Recharge
              </Button>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RECHARGE;
